import { useState } from "react";
import { BookOpen, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { OutlookSteps } from "./OutlookSteps";
import { HostnetSteps } from "./HostnetSteps";

const STATUS_ROWS = [
  ["Toestemming gevraagd", "Toestemmingsmail verstuurd via Hostnet, wacht op antwoord"],
  ["Toestemming gegeven", "Antwoordde ja — mag Mail 1, 2 en 3 ontvangen"],
  ["Geen toestemming", "Antwoordde nee — krijgt geen mails meer"],
  ["In wachtrij", "Ingeschreven, klaar voor mail 1"],
  ["Loopt", "Campagne actief, volgende mail is gepland"],
  ["Gereageerd", "Lead heeft geantwoord — uit de verzendlijst"],
  ["Klant", "Conversie — lead is klant geworden"],
  ["Gepauzeerd", "Tijdelijk uit de reeks"],
  ["Afgerond", "Alle 3 mails verstuurd, geen reactie"],
  ["Afgemeld", "Uitgeschreven — mag niet meer gemaild"],
];

const Phase = ({ n, title, children }) => (
  <div className="relative pl-10">
    <span className="absolute left-0 top-0 flex h-7 w-7 items-center justify-center rounded-full bg-[#FF4500] text-white text-sm font-bold">
      {n}
    </span>
    <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
    <div className="mt-1.5 text-sm text-slate-600 space-y-1.5 leading-relaxed">{children}</div>
  </div>
);

export const WorkflowGuide = ({ open: openProp, onOpenChange, hideButton }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;
  return (
    <>
      {!hideButton && (
        <Button
          data-testid="workflow-guide-button"
          variant="ghost"
          onClick={() => setOpen(true)}
          className="rounded-full text-slate-500 hover:text-[#FF4500]"
          title="Werkproces: van lead naar campagne"
        >
          <BookOpen className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Werkproces
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="workflow-guide-dialog"
          className="w-[calc(100vw-1rem)] max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Werkproces: van lead naar campagne
            </DialogTitle>
            <DialogDescription>
              Zo loopt een lead door de app — van scrapen tot versturen, reageren en afmelden.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg bg-orange-50 border border-orange-200 px-4 py-3 text-sm text-orange-900">
            <strong>Kernprincipe:</strong> er gaat nooit mail via een verzenddienst. Je verstuurt altijd vanaf je
            <strong> eigen Hostnet-adressen</strong>: <strong>Route A</strong> laat de app dat rustig en automatisch doen
            via je eigen mailbox, <strong>Route B</strong> gaat via de CSV-export en je Outlook-add-on.
            Begin bij voorkeur met een <strong>Toestemmingsmail</strong> en stuur Mail 1–3 alleen naar wie "ja" zegt.
          </div>

          <div className="mt-6 space-y-7">
            <Phase n={1} title="Lead binnenhalen (scrapen)">
              <p>Scrape een profiel (Watersport/Sport/VvE/Kerk) of plak eigen URL's. De app haalt
                <strong> e-mail, clubnaam, website en plaats</strong> op.</p>
              <p>Dubbele records worden samengevoegd. Elk adres krijgt een label
                <strong> Persoonlijk</strong> (penningmeester@) of <strong>Algemeen</strong> (info@).</p>
              <p>Bij een functie-adres (penningmeester@, secretaris@, voorzitter@ …) vult de app de kolommen
                <strong> Functie</strong> en <strong>Contactpersoon</strong> (naam uit het bestuur). Beide zijn aan te passen.</p>
            </Phase>

            <Phase n={2} title="Campagne starten (inschrijven)">
              <p>Selecteer leads → <strong>Inschrijven</strong> → status <strong>In wachtrij</strong>.
                Vaste reeks van 3 mails:</p>
              <ul className="list-disc pl-5">
                <li>Mail 1 — direct</li>
                <li>Mail 2 — <strong>+4 dagen</strong></li>
                <li>Mail 3 — <strong>+5 dagen later (dag 9)</strong></li>
              </ul>
              <p>Teksten beheer je per profiel bij <strong>Templates</strong>.</p>
            </Phase>

            <Phase n={3} title="De mails écht versturen (eigen account)">
              <p><strong>Via Hostnet (Route A):</strong> Inplannen via Hostnet → de app verstuurt in een rustig tempo vanaf je eigen
                mailbox. Eerst de <strong>Toestemmingsmail</strong>, daarna Mail 1–3 voor wie toestemming gaf. Zie het stappenplan hieronder.</p>
              <p><strong>Verzendhulp:</strong> wachtrij (persoonlijke adressen bovenaan) →
                "Kopieer deze mail" → plak/verstuur vanuit je eigen account.</p>
              <p><strong>CSV mail-merge:</strong> exporteer de stap → importeer in je eigen
                verzendtool → verstuur in bulk.</p>
              <p>Na verzending gaat de lead op <strong>Loopt</strong>; leads voor mail 2/3
                verschijnen onder <strong>Opvolgen</strong>.</p>
            </Phase>

            <Phase n={4} title="De lead reageert">
              <p>Antwoord op de Toestemmingsmail? Klik <strong>Toestemming ✓</strong> of <strong>Nee</strong> in de tabel.</p>
              <p>Markeer handmatig op <strong>Gereageerd</strong> (+ leg contactpersoon vast), of
                gebruik <strong>Reacties importeren (CSV)</strong>.</p>
              <p>De lead valt uit de verzendlijst → <strong>geen mail 2 of 3 meer</strong>.</p>
            </Phase>

            <Phase n={5} title="Status bijwerken">
              <p>Zet de lead op de juiste eindstatus: <strong>Klant</strong>,
                <strong> Gepauzeerd</strong>, <strong>Afgerond</strong> of <strong>Afgemeld</strong>.</p>
            </Phase>

            <Phase n={6} title="Klant worden of afmelden">
              <p><strong>Wordt klant:</strong> status <strong>Klant</strong> → geen campagnemails
                meer, telt mee als conversie.</p>
              <p><strong>Meldt zich af:</strong> via de afmeldlink in de mail (naar je eigen domein)
                gaat de lead <strong>automatisch</strong> op <strong>Afgemeld</strong> en wordt
                uitgesloten van alle campagnes.</p>
            </Phase>
          </div>

          <HostnetSteps />
          <OutlookSteps />

          <div className="mt-7 rounded-lg bg-slate-50 border border-slate-200 p-4">
            <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-600">
              {["Scrapen", "Functie & naam", "Toestemming vragen", "Toestemming ✓", "Mail 1-2-3", "Reageert", "Klant / Afgemeld"].map((s, i, a) => (
                <span key={s} className="flex items-center gap-2">
                  <span className="rounded-full bg-white border border-slate-300 px-3 py-1">{s}</span>
                  {i < a.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-slate-400" />}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <h4 className="font-display text-sm font-bold text-slate-900 mb-2">Statusoverzicht</h4>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <tbody>
                  {STATUS_ROWS.map(([label, desc], i) => (
                    <tr key={label} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                      <td className="px-4 py-2 font-medium text-slate-900 w-40 sm:whitespace-nowrap sm:w-48">{label}</td>
                      <td className="px-4 py-2 text-slate-600">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WorkflowGuide;
